import { roundAbnt } from './math';

/** Filtra por valores únicos. Usado com o 'Array.prototype.filter' */
export function arrayFilterUnique(
    value: string | number,
    index: number,
    self: (string | number)[],
): boolean {
    return self.indexOf(value) === index;
}

/** Combinação de Array.find e Array.findIndex */
export function findItemAndIndex<T>(
    array: T[],
    predicate: (item: T, index: number) => boolean,
): { index: number; item: T } | null {
    const index = array.findIndex(predicate);

    if (index === -1) {
        return null;
    }

    return { index, item: array[index] as T };
}

/** Returns the minimum item in an array based on the iteratee function. */
export function minBy<T>(array: T[], iteratee: (item: T) => number): T | undefined {
    if (array.length === 0) {
        return undefined;
    }

    let minItem = array[0] as T;
    let minValue = iteratee(minItem);

    for (const item of array) {
        const currentValue = iteratee(item);

        if (currentValue < minValue) {
            minValue = currentValue;
            minItem = item;
        }
    }

    return minItem;
}

type NumericKeyOf<T> = {
    [K in keyof T]-?: Exclude<T[K], null | undefined> extends number ? K : never;
}[keyof T];

export function rateio<T extends Record<string, unknown>>(
    list: T[],
    fieldTotal: NumericKeyOf<T>,
    fieldRateio: NumericKeyOf<T>,
    total: number,
    precision: number = 2,
): void {
    if (list.length === 0) {
        return;
    }

    const setRateio = (item: T, value: number): void => {
        (item as Record<PropertyKey, unknown>)[fieldRateio as PropertyKey] = value;
    };

    const totalBase = list.reduce((acc, item) => {
        const value = Number(item[fieldTotal] ?? 0);
        return acc + (Number.isFinite(value) ? value : 0);
    }, 0);

    if (totalBase <= 0 || total <= 0) {
        for (const item of list) {
            setRateio(item, 0);
        }

        return;
    }

    let acumulado = 0;
    const lastIndex = list.length - 1;

    for (const [index, item] of list.entries()) {
        if (index === lastIndex) {
            setRateio(item, roundAbnt(total - acumulado, precision));
            continue;
        }

        const value = Number(item[fieldTotal] ?? 0);
        const safeValue = Number.isFinite(value) ? value : 0;
        const valorRateio = roundAbnt((safeValue / totalBase) * total, precision);
        acumulado = roundAbnt(acumulado + valorRateio, precision);
        setRateio(item, valorRateio);
    }
}
